import AnimatedBackground from '@/components/background/AnimatedBackground';
import AuthStatus from '@/components/layout/AuthStatus';
import GenerationPageDrawer from '@/components/layout/GenerationPageDrawer';
import { PageLayout } from '@/components/layout/PageLayout';
import CreationFormModal from '@/components/modals/CreationFormModal';
import SignInModal from '@/components/modals/SignInModal';
import { ChevronRight, Menu, Sparkle } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// Hook to automatically save the prompt
const useSavePromptLocally = ({ prompt }: { prompt: string }) => {
    const savePrompt = (promptText: string, draft: boolean) => {
        if (!promptText.trim()) return;

        const existingPrompts = JSON.parse(localStorage.getItem('generatePagePrompts') || '[]');
        const newPrompt = {
            id: Date.now().toString(),
            text: promptText.trim(),
            draft: draft,
        };

        const updatedPrompts = [newPrompt, ...existingPrompts];
        localStorage.setItem('generatePagePrompts', JSON.stringify(updatedPrompts));
    };

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            savePrompt(prompt, true);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [prompt]);

    return {
        savePrompt,
    };
};

export default function Home() {
    const [prompt, setPrompt] = useState('');
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<'generate' | 'create' | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const { status, data: session } = useSession();
    const isUserLoggedIn = status === 'authenticated';
    const router = useRouter();

    // const { getAllPrompts, savePrompt, removePrompt, clearAllPrompts } = useSavePromptLocally({ prompt });
    const { savePrompt } = useSavePromptLocally({ prompt });

    useEffect(() => {
        const savedPendingAction = localStorage.getItem('generatePagePendingAction');
        if (savedPendingAction && (savedPendingAction === 'generate' || savedPendingAction === 'create')) {
            setPendingAction(savedPendingAction as 'generate' | 'create');
        }

        // Listen for load prompt events from the drawer
        const handleLoadPrompt = (event: CustomEvent) => {
            setPrompt(event.detail);
            // Close the drawer after loading the prompt
            document.getElementById('drawer')?.click();
        };

        window.addEventListener('loadPrompt', handleLoadPrompt as EventListener);

        return () => {
            window.removeEventListener('loadPrompt', handleLoadPrompt as EventListener);
        };
    }, []);

    useEffect(() => {
        if (isUserLoggedIn && pendingAction && session) {
            if (pendingAction === 'create') {
                setIsCreateModalOpen(true);
            } else if (pendingAction === 'generate') {
                handleGenerateForm();
            }
            localStorage.removeItem('generatePagePendingAction');
            setPendingAction(null);
        }
    }, [isUserLoggedIn, pendingAction, session, router]);

    const handleGenerateForm = async () => {
        if (!prompt.trim()) return;

        setIsGenerating(true);
        savePrompt(prompt, false);

        try {
            const response = await fetch('/api/forms/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prompt: prompt.trim(),
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to generate form');
                throw new Error(errorData.error || 'Failed to generate form');
            }
            const generatedForm = await response.json();
            toast.success('Form generated successfully, redirecting to form edition...');
            router.push(`/form/${generatedForm.id}/edit`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'An error occurred while generating the form');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAction = (action: 'generate' | 'create') => {
        if (!isUserLoggedIn) {
            setPendingAction(action);
            localStorage.setItem('generatePagePendingAction', action);
            setIsSignInModalOpen(true);
        } else {
            if (action === 'create') {
                setIsCreateModalOpen(true);
            } else {
                handleGenerateForm();
            }
        }
    };

    const handleSignInModalClose = () => {
        setIsSignInModalOpen(false);
        if (!isUserLoggedIn) {
            localStorage.removeItem('generatePagePendingAction');
            setPendingAction(null);
        }
    };

    const handlePromptClick = (promptText: string) => {
        // Clear current prompt
        setPrompt('');

        // Type the new prompt character by character
        let currentIndex = 0;
        const typingInterval = setInterval(() => {
            if (currentIndex < promptText.length) {
                setPrompt(promptText.slice(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingInterval);
            }
        }, 20); // 20ms delay between characters
    };

    const promptIdeas = [
        'Build a bank credit simulator',
        'Design a ROI calculator for investments',
        'Design an energy cost calculator',
        'Create a savings goal calculator',
        'Design a retirement planning tool',
        'Build a tax calculator',
        'Create a transportation cost simulator',
    ];

    return (
        <PageLayout className="relative overflow-hidden">
            <AnimatedBackground />

            <div className="drawer h-screen flex items-center relative z-10">
                <div className="absolute right-8 top-8">
                    <AuthStatus singInCallbackUrl={router.asPath} logoutCallbackUrl={router.asPath} />
                </div>
                <input id="drawer" type="checkbox" className="drawer-toggle" />
                <div className="drawer-content w-full">
                    {isUserLoggedIn && (
                        <label htmlFor="drawer" className="drawer-button btn btn-ghost btn-sm absolute top-8 left-8">
                            <Menu />
                        </label>
                    )}
                    <div className="flex flex-col w-3/5 mx-auto p-5 rounded-md">
                        <h1 className="my-5 px-10 xl:px-30 text-xl xl:text-3xl text-center font-bold text-stone-800">
                            Turn visitors into leads with interactive calculators
                        </h1>

                        <div className="mt-2 mb-5">
                            <p className="text-stone-700 mb-3 text-center">Quick ideas to get started:</p>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {promptIdeas.map((idea, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handlePromptClick(idea)}
                                        className="btn btn-sm btn-outline btn-neutral hover:text-white transition-all duration-200 text-xs font-normal px-3 py-1 rounded"
                                    >
                                        {idea}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <textarea
                            name="generator"
                            id="generator"
                            className="textarea w-full rounded my-3 p-5 focus:outline-none resize-none border border-gray-300 transition-all duration-300"
                            placeholder="Ask calculator to create a simulator of..."
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={4}
                        />
                        <div className="grid grid-cols-2 gap-5 max-w-md mx-auto">
                            <button
                                onClick={() => handleAction('generate')}
                                className="btn btn-primary"
                                disabled={prompt.trim().length === 0}
                            >
                                {isGenerating ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        Generate
                                        <Sparkle size={15} />
                                    </>
                                )}
                            </button>
                            <button onClick={() => handleAction('create')} className="btn btn-ghost text-stone-400">
                                Or do it yourself
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                </div>
                {isUserLoggedIn && (
                    <div className="drawer-side">
                        <label htmlFor="drawer" aria-label="close sidebar" className="drawer-overlay"></label>
                        <GenerationPageDrawer />
                    </div>
                )}
            </div>

            <CreationFormModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
            <SignInModal isOpen={isSignInModalOpen} onClose={handleSignInModalClose} callbackUrl={router.asPath} />
        </PageLayout>
    );
}
